import catalog from "./permissions.generated.json"

// Werte aus permissions.generated.json (Kopie von 1001-01-backend/docs/migration/permissions.json).
// JSON-Importe sind in TS nur `string[]`, deshalb stehen die Unions hier als Literale; ability.test.ts prüft die Übereinstimmung mit dem JSON.
export const ACTIONS = [
    "manage",
    "create",
    "read",
    "update",
    "delete",
    "upload",
    "download",
    "readRestricted",
    "updateRestricted",
    "readOU",
    "updateOU",
    "managePermissions",
    "generateQrCode",
    "scopeSelf",
    "scopeCostCenter",
    "scopeAll",
    "checkInOut",
    "manualCheckInOut",
    "execute",
    "administer",
    "optimizeRoute",
    "readColor",
    "exportAll",
    "access",
] as const

export const SUBJECTS = [
    "all",
    "settings",
    "permission",
    "user",
    "role",
    "history",
    "apiKey",
    "dashboard",
    "employee",
    "accounting",
    "payment",
    "object",
    "customer",
    "costCenter",
    "task",
    "absence",
    "session",
    "orderRequest",
    "help",
    "report",
    "file",
    "customerPortal",
] as const

export type Action = (typeof ACTIONS)[number]
export type Subject = (typeof SUBJECTS)[number]

export type TPermission = {
    action: Action
    subject: Subject
    /** Recht gilt nur für die Ressource mit dieser `_id` (CASL-Bedingung). */
    subjectId?: string
}

// Schlüssel = Namen des früheren Rollen-Enums (Stand 306f011), Wert = dessen rbac-Rolle; damit bleibt der Umbau mechanisch.
const LEGACY_ROLES = {
    dashboard_read: "rbac_dashboard_read",
    mitarbeiter_read: "rbac_mitarbeiter_read",
    mitarbeiter_read_ou: "rbac_mitarbeiter_read_ou",
    mitarbeiter_read_write: "rbac_mitarbeiter_read_write",
    mitarbeiter_read_write_ou: "rbac_mitarbeiter_read_write_ou",
    mitarbeiter_delete: "rbac_mitarbeiter_delete",
    mitarbeiter_create: "rbac_mitarbeiter_create",
    mitarbeiter_manage_permissions: "rbac_mitarbeiter_manage_permissions",
    buchhaltung_read_restricted: "rbac_buchhaltung_read_restricted",
    buchhaltung_read_write_restricted: "rbac_buchhaltung_read_write_restricted",
    buchhaltung_read: "rbac_buchhaltung_read",
    buchhaltung_read_write: "rbac_buchhaltung_read_write",
    buchhaltung_zahlungen_read: "rbac_buchhaltung_zahlungen_read",
    buchhaltung_zahlungen_read_write: "rbac_buchhaltung_zahlungen_read_write",
    objekte_read: "rbac_objekte_read",
    objekte_read_write: "rbac_objekte_read_write",
    objekte_delete: "rbac_objekte_delete",
    objekte_create: "rbac_objekte_create",
    objekte_qr_code_generate: "rbac_objekte_qr_code_generate",
    kunden_read: "rbac_kunden_read",
    kunden_read_write: "rbac_kunden_read_write",
    kunden_delete: "rbac_kunden_delete",
    kunden_create: "rbac_kunden_create",
    aufgaben_read: "rbac_task_read",
    aufgaben_read_write: "rbac_task_read_write",
    aufgaben_create: "rbac_task_create",
    aufgaben_delete: "rbac_task_delete",
    aufgaben_permissions_self: "rbac_taskPermission_self",
    aufgaben_permissions_costcenter: "rbac_taskPermission_costcenter",
    aufgaben_permissions_all: "rbac_taskPermission_all",
    abwesenheiten_read: "rbac_absence_read",
    abwesenheiten_read_write: "rbac_absence_read_write",
    abwesenheiten_create: "rbac_absence_create",
    abwesenheiten_permissions_self: "rbac_absencePermission_self",
    abwesenheiten_permissions_costcenter: "rbac_absencePermission_costcenter",
    abwesenheiten_permissions_all: "rbac_absencePermission_all",
    termine_checkin: "rbac_session_check_in_out",
    termine_manual_checkin: "rbac_session_manual_check_in_out",
    termine_execute: "rbac_session_execute",
    termine_management: "rbac_session_management",
    anfragen: "rbac_orderRequest_read_write",
    niederlassungen_read: "rbac_costcenter_read",
    niederlassungen_read_write: "rbac_costcenter_read_write",
    niederlassungen_delete: "rbac_costcenter_delete",
    niederlassungen_create: "rbac_costcenter_create",
    help_read_write: "rbac_help_read_write",
    global_settings_read_write: "rbac_global_settings_read_write",
    rbac_export_all_files: "rbac_export_all_files",
} as const

export type TPermissionKey = keyof typeof LEGACY_ROLES

const RBAC = catalog.rbac as Record<string, TPermission>

/** rbac-Rolle (z. B. "rbac_buchhaltung_read") -> {action, subject} laut Katalog. */
export const permissionForRbacRole = (role: string): TPermission | undefined => {
    const permission = RBAC[role]
    return permission ? { action: permission.action, subject: permission.subject } : undefined
}

/** `P.buchhaltung_read` = `{ action: "read", subject: "accounting" }` usw. */
export const P = Object.fromEntries(Object.entries(LEGACY_ROLES).map(([key, role]) => [key, permissionForRbacRole(role)])) as Record<TPermissionKey, TPermission>
